import type { Middleware, MiddlewareReturn, MiddlewareState } from '@floating-ui/react';
import { detectOverflow, getOverflowAncestors } from '@floating-ui/react';

type Side = 'top' | 'bottom' | 'left' | 'right';

export interface StickyMiddlewareOptions {
    /** When true, the popover should reposition rather than close on scroll. */
    enabled: boolean;
    /** Called when the popover should close due to scroll events (non-sticky mode). */
    onDismiss?: () => void;
    /** Extra padding to keep between the popover and viewport edges when clamping. */
    padding?: number;
    /** The boundary to clamp the popover to. */
    boundary?: Element;
}

async function clampToViewport(
    state: MiddlewareState,
    padding = 0,
    boundary?: Element
): Promise<Pick<MiddlewareReturn, 'x' | 'y'>> {
    const overflow = await detectOverflow(state, {
        boundary,
        padding,
    });

    let nextX = state.x;
    let nextY = state.y;

    if (overflow.top > 0) {
        nextY += overflow.top;
    } else if (overflow.bottom > 0) {
        nextY -= overflow.bottom;
    }

    if (overflow.left > 0) {
        nextX += overflow.left;
    } else if (overflow.right > 0) {
        nextX -= overflow.right;
    }

    return { x: nextX, y: nextY };
}

function isElement(node: unknown): node is Element {
    return typeof Element !== 'undefined' && node instanceof Element;
}

type MiddlewareWithEffect = Middleware & {
    effect?: (args: {
        options: StickyMiddlewareOptions;
        elements: MiddlewareState['elements'];
    }) => void | (() => void);
};

export interface StickyMiddlewareData {
    suppressFlip?: boolean;
}

export function sticky({ ...options }: StickyMiddlewareOptions): Middleware {
    const middleware: MiddlewareWithEffect = {
        name: 'sticky',
        options,
        async fn(state) {
            if (!options.enabled) {
                return {};
            }

            const placementSide = state.placement.split('-')[0] as Side;

            const [coords, referenceOverflow] = await Promise.all([
                clampToViewport(state, options.padding, options.boundary),
                detectOverflow(state, {
                    boundary: options.boundary,
                    elementContext: 'reference',
                    padding: options.padding,
                }),
            ]);

            const suppressFlip = (referenceOverflow[placementSide] ?? 0) > 0;

            return {
                ...coords,
                data: {
                    suppressFlip,
                } satisfies StickyMiddlewareData,
            } satisfies MiddlewareReturn;
        },
        effect({ elements, options: stickyOptions }) {
            if (stickyOptions.enabled || !stickyOptions.onDismiss) {
                return undefined;
            }

            const { reference, floating } = elements;

            if (!isElement(reference)) {
                return undefined;
            }

            const listener = () => {
                stickyOptions.onDismiss?.();
            };

            const ancestors = new Set([
                ...getOverflowAncestors(reference),
                ...getOverflowAncestors(floating),
            ]);

            const eventOptions: AddEventListenerOptions = { passive: true };

            ancestors.forEach((ancestor) => {
                if ('addEventListener' in ancestor) {
                    ancestor.addEventListener('scroll', listener, eventOptions);
                }
            });

            return () => {
                ancestors.forEach((ancestor) => {
                    if ('removeEventListener' in ancestor) {
                        ancestor.removeEventListener('scroll', listener, eventOptions);
                    }
                });
            };
        },
    };

    return middleware;
}
